import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Tooltip,
  TablePagination,
  Typography,
  Divider,
  Avatar,
} from "@mui/material";
import { DeleteOutline, EditOutlined, Add } from "@mui/icons-material";
import APIURL, { IMAGE_BASE_URL } from "../api/apiClient";
import SubCategoryModal from "./SubCategory";
import CategoryEditDialog from "./CategoryEditDialog";
import { message } from "antd";
import Loader from "./Loader";

const getCategoryImage = (path) => {
  if (!path) {
    return "";
  }

  return path.startsWith("http") ? path : `${IMAGE_BASE_URL}${String(path).replace(/^\/+/, "")}`;
};

const normalizeApiResponse = (response) => {
  if (typeof response !== "string") {
    return response;
  }

  try {
    return JSON.parse(response.trim() || "{}");
  } catch {
    return { status: false, message: response };
  }
};

const CategoryTable = ({ refreshTrigger }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editCategory, setEditCategory] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const fetchCategories = async () => {
    setLoading(true);

    try {
      const result = await APIURL.get("/fetchcategories.php");
      const normalizedResult = normalizeApiResponse(result);
      const categoriesArray = Array.isArray(normalizedResult) ? normalizedResult : [];

      const categoriesWithSubs = await Promise.all(
        categoriesArray.map(async (category) => {
          const subCategoryResponse = await APIURL.get(`/subCategories.php?category_id=${category.id}`);
          const subcategories = Array.isArray(subCategoryResponse)
            ? subCategoryResponse
            : subCategoryResponse?.data || [];

          return { ...category, subcategories };
        })
      );

      setCategories(categoriesWithSubs);
    } catch (error) {
      console.error("Error fetching categories:", error);
      message.error("Unable to load categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [refreshTrigger]);

  const paginatedCategories = useMemo(
    () => categories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [categories, page, rowsPerPage]
  );

  const handleDeleteMainCategory = async (categoryId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this category?");
    if (!confirmDelete) {
      return;
    }

    try {
      const response = await APIURL.delete(`/fetchcategories.php?id=${categoryId}`);
      const parsedResponse = normalizeApiResponse(response);

      if (parsedResponse.status === true || parsedResponse.status === "true") {
        message.success(parsedResponse.message);
        fetchCategories();
      } else {
        message.error(parsedResponse.message || "Unable to delete category.");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      message.error("Something went wrong");
    }
  };

  const handleAddSubcategory = async (data) => {
    try {
      const response = await APIURL.post("/subCategories.php", data);
      if (response.status) {
        message.success(response.message);
        setOpenModal(false);
        fetchCategories();
      } else {
        message.error(response.message);
      }
    } catch (error) {
      console.error("Error:", error);
      message.error("Something went wrong");
    }
  };

  const handleDeleteSubcategory = async (subId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this subcategory?");
    if (!confirmDelete) {
      return;
    }

    try {
      const response = await APIURL.delete(`/subCategories.php?id=${subId}`);
      if (response.status) {
        message.success(response.message);
        fetchCategories();
      } else {
        message.error(response.message);
      }
    } catch (error) {
      console.error("Error:", error);
      message.error("Something went wrong");
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{ width: "100%", overflowX: "auto", p: 3, borderRadius: 2, boxShadow: 3 }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f3f3f3" }}>
              <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Image</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Category Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Featured</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 4 }}>
                    <Loader type="table" loading={true} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No categories to display
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedCategories.map((category, index) => (
                <React.Fragment key={category.id}>
                  <TableRow hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      {category.image ? (
                        <Avatar
                          variant="rounded"
                          src={getCategoryImage(category.image)}
                          alt={category.name}
                          sx={{ width: 56, height: 42 }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No image
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{category.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={Number(category.status) === 1 ? "Active" : "Inactive"}
                        color={Number(category.status) === 1 ? "success" : "default"}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={Number(category.featured) === 1 ? "Computer" : "Laptop"}
                        color={Number(category.featured) === 1 ? "primary" : "secondary"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                      <Tooltip title="Edit Category">
                        <IconButton color="primary" onClick={() => setEditCategory(category)}>
                          <EditOutlined />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Category">
                        <IconButton sx={{ color: "red" }} onClick={() => handleDeleteMainCategory(category.id)}>
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={6} sx={{ background: "#fafafa", py: 1.5 }}>
                      <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                        {category.subcategories.length > 0 ? (
                          category.subcategories.map((subCategory) => (
                            <Chip
                              key={subCategory.id}
                              label={subCategory.name}
                              onDelete={() => handleDeleteSubcategory(subCategory.id)}
                              color="primary"
                              size="small"
                              variant="outlined"
                              sx={{ borderRadius: 1.5 }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No subcategories
                          </Typography>
                        )}
                        <Tooltip title="Add Subcategory">
                          <IconButton
                            size="small"
                            sx={{
                              ml: category.subcategories.length ? 1 : 0,
                              border: "1px dashed #1976d2",
                              color: "#1976d2",
                            }}
                            onClick={() => {
                              setSelectedCategory(category);
                              setOpenModal(true);
                            }}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0 }}>
                      <Divider />
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={categories.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 15]}
        sx={{ mt: 2 }}
      />

      {selectedCategory ? (
        <SubCategoryModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          categoryId={selectedCategory.id}
          categoryName={selectedCategory.name}
          onSubmit={handleAddSubcategory}
        />
      ) : null}

      {editCategory ? (
        <CategoryEditDialog
          open={!!editCategory}
          category={editCategory}
          onClose={() => setEditCategory(null)}
          onSaved={fetchCategories}
        />
      ) : null}
    </Paper>
  );
};

export default CategoryTable;
